/* eslint-disable camelcase */
import React, { useState } from 'react';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import { IDescription, IOption } from '../../types/customizer';
import If from '../If';
import MaterialInfo from '../SwatchCategory/MaterialInfo';
import './index.scss';
import AppModal from '../AppModal';

interface ISwatchOption extends IOption {
  hasSwatch: boolean;
  fieldType: string;
  onChange: (updated: any) => void;
  selectedOption: IOption;
  isChanging: string;
  moreInfo?: IDescription | null;
  hideMoreInfo?: boolean;
  onSelectedClick?: () => void;
}

const SwatchOption = ({
  type,
  name,
  short_name,
  swatch_url,
  selected,
  sku,
  additionalClassName,
  hasSwatch,
  fieldType,
  onChange,
  selectedOption,
  isChanging,
  moreInfo,
  hideMoreInfo,
  onSelectedClick
}: ISwatchOption) => {
  const [open, setOpen] = useState(false);

  const onClose = () => {
    setOpen(false);
  };

  const openModal = () => {
    setOpen(true);
  };

  const handleClick = () => {
    if (!selected) {
      onChange({ [fieldType]: sku });
    } else if (onSelectedClick) {
      onSelectedClick();
    } else {
      if (selectedOption.has_inside_reversed_option || selectedOption.has_outside_reversed_option) {
        if (selectedOption.is_reverse_selected) {
          onChange({ [fieldType]: sku });
        } else {
          onChange({ [fieldType]: sku + 'REV' });
        }
      }
    }
  };

  const getUrl = (swatch_url: string) => {
    return swatch_url.indexOf('http') !== -1
      ? swatch_url
      : `${process.env.REACT_APP_ASSET_URL}${swatch_url}?image_size=180x180`;
  };

  if (hasSwatch) {
    // One width at every breakpoint now (was 90px below sm, 112px above). 112px is what
    // "Superconductor" needs to sit on one line at the label's mobile size: ~92px of text
    // plus the 12px of xs padding.
    return (
      <div className={clsx({ 'tw-w-28 tw-text-center sm:tw-mr-5 tw-my-2 tw-relative': true })}>
        <div className="xs:tw-px-1.5 sm:tw-px-2">
          <div
            className={clsx('tw-inline-block tw-relative', {
              'tw-border-2 tw-border-solid tw-border-brand-color tw-rounded-full': selected && !isChanging,
              'selected-border': isChanging.includes(sku)
            })}
            onClick={handleClick}
          >
            <div
              className="tw-block tw-relative xs:tw-w-16 xs:tw-h-16 sm:tw-w-22.5 sm:tw-h-22.5 tw-rounded-full tw-cursor-pointer tw-bg-no-repeat tw-bg-center tw-bg-contain tw-m-1"
              style={{ backgroundImage: `url(${getUrl(swatch_url)})` }}
            >
              {!isChanging && selected && moreInfo && !hideMoreInfo && (
                <div
                  onClick={openModal}
                  className="moreInfo-overlay tw-bg-brand-color xs:tw-text-[8px] sm:tw-text-[11px] xs:tw-pt-0.5 sm:tw-pt-2"
                >
                  LEARN MORE
                </div>
              )}
            </div>
          </div>
          {/* Show only for md devices (768px) */}
          <div className="secondary-font-family tw-text-sm tw-text-primary-color tw-mt-1 tw-pl-1 md:tw-block tw-hidden">
            {name}
          </div>

          {/* Show only for less than md devices (768px) */}
          {/* text-xs, and no pl-1: at 12px "Superconductor" measures ~92px against the
              100px this cell leaves after padding, so it fits on one line — the 4px of
              left padding both ate into that budget and pushed the text off-centre
              inside a text-center cell.
              hyphens-auto + break-words stay as the safety net for any name longer than
              that, breaking mid-word with a hyphen rather than truncating or spilling
              over the neighbouring options. Deliberately not applied to the md+ label
              above, which has its own width and size. */}
          <div className="secondary-font-family tw-text-xs tw-text-primary-color tw-mt-1 md:tw-hidden tw-block tw-hyphens-auto tw-break-words">
            {short_name || name}
          </div>
        </div>
        <AppModal size="xl" isOpen={open} onClose={onClose} adjustToContent>
          {moreInfo && <MaterialInfo {...moreInfo} />}
        </AppModal>
      </div>
    );
  } else {
    return (
      <div
        className={clsx({
          'tw-p-1.5 sm:tw-p-2 tw-cursor-pointer tw-relative': true,
          'selected-border-square ': isChanging === sku
        })}
      >
        <If condition={name || sku}>
          <div
            className={twMerge(
              clsx({
                'tw-p-1.5 sm:tw-p-2 tw-border tw-border-solid tw-text-center min-w-18 secondary-font-family tw-text-sm tw-text-primary-color': true,
                'tw-border-secondary-border-color': !selected || isChanging,
                'tw-border-2 tw-border-solid tw-border-brand-color': selected && !isChanging
              }),
              `${additionalClassName}`
            )}
            onClick={handleClick}
          >
            {sku === 'CUSTOM' ? sku : name}
          </div>
        </If>
      </div>
    );
  }
};

export default SwatchOption;
